import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

export type ToastTone = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  tone: ToastTone
  text: string
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  toast: (input: { tone?: ToastTone; text: string; action?: Toast['action']; durationMs?: number }) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((arr) => arr.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    ({ tone = 'info', text, action, durationMs = 3200 }: {
      tone?: ToastTone
      text: string
      action?: Toast['action']
      durationMs?: number
    }) => {
      const id = nextId++
      setToasts((arr) => [...arr, { id, tone, text, action }])
      if (durationMs > 0) {
        setTimeout(() => dismiss(id), durationMs)
      }
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // Stack above the bottom nav so toasts are visible on mobile.
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'calc(var(--nav-height-mobile) + 12px + env(safe-area-inset-bottom, 0))',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          pointerEvents: 'none',
          zIndex: 999,
          padding: '0 12px',
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{
              pointerEvents: 'auto',
              background:
                t.tone === 'success' ? 'var(--color-success-soft)' :
                t.tone === 'error'   ? 'var(--color-danger-soft)'  :
                                       'var(--color-surface)',
              color:
                t.tone === 'success' ? 'var(--color-success-text)' :
                t.tone === 'error'   ? 'var(--color-danger-text)'  :
                                       'var(--color-text)',
              border: '1px solid ' + (
                t.tone === 'success' ? 'var(--color-success)' :
                t.tone === 'error'   ? 'var(--color-danger)'  :
                                       'var(--color-border)'
              ),
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
              maxWidth: 480,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              animation: 'pt-toast-in 200ms ease-out',
            }}
          >
            <span style={{ flex: 1 }}>
              {t.tone === 'success' ? '✓ ' : t.tone === 'error' ? '✗ ' : ''}
              {t.text}
            </span>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action!.onClick()
                  dismiss(t.id)
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  fontWeight: 700,
                  fontSize: 13,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                opacity: 0.6,
                cursor: 'pointer',
                fontSize: 16,
                padding: 0,
                lineHeight: 1,
                fontFamily: 'inherit',
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Outside provider — return a no-op so tests/storybooks don't crash.
    return { toast: () => {} }
  }
  return ctx
}
